import { BarRounded } from '@visx/shape';
import { useMemo } from 'react';
import { animated, useTransition } from '@react-spring/web';
import { cleanColor, colorHasUrl } from "../../../utils/cleanColorString.js";
import getScaleBaseline from "../../../utils/getScaleBaseline.js";
import AnimatedPath from "./AnimatedPath.js";
import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
function enterUpdate(_ref) {
  let {
    x,
    y,
    width,
    height,
    fill
  } = _ref;
  return {
    x,
    y,
    width,
    height,
    fill: cleanColor(fill),
    opacity: 1
  };
}
function useBarTransitionConfig(_ref2) {
  let {
    scale,
    horizontal
  } = _ref2;
  const shouldAnimateX = !!horizontal;
  return useMemo(() => {
    const scaleBaseline = getScaleBaseline(scale);
    function fromLeave(_ref3) {
      let {
        x,
        y,
        width,
        height,
        fill
      } = _ref3;
      return {
        x: shouldAnimateX ? scaleBaseline ?? 0 : x,
        y: shouldAnimateX ? y : scaleBaseline ?? 0,
        width: shouldAnimateX ? 0 : width,
        height: shouldAnimateX ? height : 0,
        fill: cleanColor(fill),
        opacity: 0
      };
    }
    return {
      unique: true,
      from: fromLeave,
      leave: fromLeave,
      enter: enterUpdate,
      update: enterUpdate,
      keys: bar => bar.key
    };
  }, [scale, shouldAnimateX]);
}
function AnimatedBarsRounded(_ref4) {
  let {
    bars,
    xScale,
    yScale,
    horizontal,
    radius,
    radiusAll,
    radiusTop,
    radiusRight,
    radiusBottom,
    radiusLeft,
    ...pathProps
  } = _ref4;
  return (
    /*#__PURE__*/
    // eslint-disable-next-line react/jsx-no-useless-fragment
    _jsx(_Fragment, {
      children: bars.map(_ref5 => {
        let {
          key,
          fill,
          x,
          y,
          width,
          height
        } = _ref5;
        return /*#__PURE__*/_jsx(BarRounded, {
          x: x,
          y: y,
          width: width,
          height: height,
          radius: radius,
          all: radiusAll,
          top: radiusTop,
          right: radiusRight,
          bottom: radiusBottom,
          left: radiusLeft,
          children: _ref6 => {
            let {
              path
            } = _ref6;
            return /*#__PURE__*/_jsx(AnimatedPath, {
              className: "visx-bar visx-bar-rounded",
              d: path,
              fill: fill,
              ...pathProps
            });
          }
        }, key);
      })
    })
  );
}
function AnimatedBarsUnrounded(_ref7) {
  let {
    bars,
    xScale,
    yScale,
    horizontal,
    radius,
    radiusAll,
    radiusTop,
    radiusRight,
    radiusBottom,
    radiusLeft,
    ...rectProps
  } = _ref7;
  const animatedBars = useTransition(bars, {
    ...useBarTransitionConfig({
      horizontal,
      scale: horizontal ? xScale : yScale
    })
  });
  const isFocusable = Boolean(rectProps.onFocus || rectProps.onBlur);
  return /*#__PURE__*/_jsx(_Fragment, {
    children: animatedBars((// @ts-expect-error x/y aren't in react-spring types (which are HTML CSS properties)
    _ref8, item, _ref9) => {
      let {
        x,
        y,
        width,
        height,
        fill,
        opacity
      } = _ref8;
      let {
        key
      } = _ref9;
      return item == null || key == null ? null : /*#__PURE__*/_jsx(animated.rect, {
        tabIndex: isFocusable ? 0 : undefined,
        className: "visx-bar",
        x: x,
        y: y,
        width: width,
        height: height
        // use the item's fill directly if it's not animate-able
        ,
        fill: colorHasUrl(item.fill) ? item.fill : fill,
        opacity: opacity,
        ...rectProps
      }, key);
    })
  });
}

/** Wrapper component which renders a Bars component depending on whether it needs rounded corners. */
export default function AnimatedBars(props) {
  return props.radius == null ? /*#__PURE__*/_jsx(AnimatedBarsUnrounded, {
    ...props
  }) : /*#__PURE__*/_jsx(AnimatedBarsRounded, {
    ...props,
    radius: props.radius
  });
}